import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { IonicModule } from '@ionic/angular';
import { Subject, Subscription } from 'rxjs';
import { throttleTime } from 'rxjs/operators';
import { ButtonCategory, ButtonColor, ButtonType } from 'src/app/shared/components/button/button.types';

/**
 * Renders a custom html button with the Mable styles applied
 */
@Component({
  selector: 'mable-button',
  standalone: true,
  imports: [CommonModule, IonicModule],
  templateUrl: './button.component.html',
  styleUrls: ['./button.component.scss'],
})
export class ButtonComponent implements OnInit, OnChanges, OnDestroy {
  /**
   * Sets a category for the button (**required**). This is the primary mechanism for configuring
   * a button as there is an angular watcher listening for changes that recalculates all the other
   * styles on change based on this value - color, outline settings etc.
   */
  @Input() category: ButtonCategory = 'orange-500';

  /**
   * Sets the html button type attribute to control whether its a clickable button or a submit button
   */
  @Input() buttonType: ButtonType = 'button';

  /**
   * An optional `disabled` flag to pass through to the ionic button
   */
  @Input() disabled = false;

  /**
   * Sets the color for **icon-only** buttons
   */
  @Input() color: ButtonColor = 'orange-500';

  /**
   * Sets an aria-label attribute for the button
   */
  @Input() ariaLabel = '';

  /**
   * The name of the icon
   */
  @Input() icon = '';

  /**
   * Passes an ionic `[slot]` value through to the button
   */
  @Input() iconSlot: 'start' | 'end' | 'icon-only' = 'start';

  /**
   * Passes an ionic `[expand]` value directly through to the button
   */
  @Input() expand: 'block' | 'full' | undefined = undefined;

  /**
   * If true applies auto margins to the buttons label, forcing it to the left of the
   * screen rather than the center.  This appears to be the only way to justify content
   * without adding another wrapper element with additional classes.
   */
  @Input() alignLeft = false;

  /**
   * If true applies auto margins to the buttons label, forcing it to the top of the
   * screen rather than the center.
   */
  @Input() alignTop = false;

  /**
   * Sets a size for the button.  Only used for **footer**, **solid-purple** and **solid-orange** buttons
   */
  @Input() size: 'small' | 'default' | 'large' = 'default';

  /**
   * Sets a router link attribute on the button
   */
  @Input() routerLink;

  /**
   * Sets a href attribute on the button
   */
  @Input() href: string;

  /**
   * Passes an ionic `[fill]` value directly through to the button
   */
  @Input() fill: 'clear' | 'solid' | 'outline' | '' = '';

  /**
   * Sets a throttle time on the button that prevents the button being clicked more than once within a certain amount of time.
   */
  @Input() throttleTime = 0;

  /**
   * An emit event to let the parent element know the button was clicked
   */
  @Output() onClick = new EventEmitter();

  // Button color is used as ion-button color property. This is different from the mable-button color input.
  buttonColor;

  shape: 'round' | undefined = undefined;

  // The click subject that will handle the throttling
  private clicks = new Subject<void>();

  // Variable to hold the subscription for click subject subscription
  private clickSubscription: Subscription;

  /**
   * Set buttons style property.
   *
   */
  setBtnProperties() {
    switch (this.category) {
      case 'purple-500':
      case 'orange-500':
        this.buttonColor = this.category;
        this.fill = 'solid';
        if (this.expand !== 'block') {
          this.size = 'default';
        }
        break;

      case 'link':
        this.buttonColor = 'purple-400';
        this.fill = 'clear';
        this.size = 'small';
        break;
      case 'header-detailed':
        this.buttonColor = 'purple-500';
        this.fill = 'clear';
        this.size = 'small';
        break;

      case 'clear-purple':
        this.buttonColor = 'purple-500';
        this.fill = 'clear';
        this.size = 'default';
        break;

      case 'outline':
        this.buttonColor = 'purple-500';
        this.fill = 'outline';
        this.size = 'default';
        break;

      case 'rounded':
        this.buttonColor = 'purple-500';
        this.fill = 'outline';
        this.size = 'small';
        this.shape = 'round';
        break;

      case 'icon-only':
        this.iconSlot = 'icon-only';
        this.fill = this.fill || 'clear';
        this.size = 'default';
        this.buttonColor = this.color;
        break;

      default:
        this.buttonColor = this.color;
    }
    if (this.disabled) {
      this.buttonColor = 'grey-300';
      this.fill = 'outline';
      this.size = 'default';
    }
  }

  ngOnInit() {
    // Call the button configuration method once on init to make sure the button renders properly
    // in instances where only a single property is set and onChanges isn't triggered.
    this.setBtnProperties();
    this.clickSubscription = this.clicks
      .pipe(throttleTime(this.throttleTime))
      .subscribe(() => this.emitThrottledClick());
  }

  // Emits throttledClick event
  emitThrottledClick() {
    this.onClick.emit();
  }

  ngOnChanges() {
    this.setBtnProperties();
  }

  // unsubsribe to the clicks subject subcription
  ngOnDestroy() {
    this.clickSubscription.unsubscribe();
  }

  /**
   * Set buttons font class.
   *
   */
  getFontClass() {
    if (this.category === 'clear-purple' || this.category === 'link') {
      return 'body-normal-16';
    }
  }

  // On click of ion button
  onButtonClick() {
    this.clicks.next();
  }
}
// Discuss with Team
// HostListener vs EventEmitter
// Accessibility
